import { MapPin, ShieldCheck, Users } from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";

export const navbarCopyPl: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Specjalizacja",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

export const aboutUsCopyPl = (
  overrides?: Partial<AboutUsCopy["header"]>,
): AboutUsCopy => ({
  header: {
    h2: "O nas",
    h3: "Polski zespół IoT, elektroniki i firmware",
    p1: "Jesteśmy software house'em z Polski specjalizującym się w produktach connected: elektronika, firmware embedded, aplikacje mobilne i backend IoT.",
    p2: "Michał i Tomasz odpowiadają za elektronikę i firmware, Kuba za backend i infrastrukturę, a Mateusz projektuje UX produktów IoT gotowych do pokazania klientom i inwestorom.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół Blues Brackets - IoT, elektronika i firmware",
    ...overrides,
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Bez pośredników - osoba na rozmowie to osoba, która projektuje PCB, pisze firmware albo buduje dashboard IoT.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy wymagania rynku UE",
      description:
        "Projektujemy z myślą o certyfikacji CE, RODO i realnym wdrożeniu produktu na rynku europejskim.",
    },
    {
      Icon: ShieldCheck,
      title: "Dbamy o bezpieczeństwo danych",
      description:
        "Dane urządzeń i użytkowników trzymamy w UE - pod kontrolą polskiego zespołu, bez zbędnych pośredników.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
});

export const contactFormPl: FooterCopy["contactForm"] = {
  heading: "Napisz do nas",
  fields: {
    name: {
      label: "Imię i nazwisko",
      placeholder: "Jan Kowalski",
      requiredError: "Podaj imię i nazwisko",
    },
    email: {
      label: "Adres e-mail",
      placeholder: "jan@firma.pl",
      requiredError: "Podaj adres e-mail",
      invalidError: "Podaj poprawny adres e-mail",
    },
    topic: {
      label: "Opis projektu",
      placeholder:
        "Krótko opisz urządzenie, elektronikę, firmware lub system IoT...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
  submit: "Wyślij zapytanie",
  submitting: "Wysyłanie...",
  successMessage: "Wiadomość wysłana!",
  errorMessage: "Nie udało się wysłać wiadomości :(",
  responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
  emailSubjectPrefix: "Imię i nazwisko",
};

export const iotClusterLinks = [
  { href: "/iot", text: "Systemy IoT" },
  {
    href: "/prototypowanie-elektroniki-i-pcb",
    text: "Prototypowanie elektroniki i PCB",
  },
  { href: "/firmware-embedded", text: "Firmware embedded" },
  { href: "/system-iot-na-zamowienie", text: "System IoT na zamówienie" },
  {
    href: "/lokalizatory-gps-i-czujniki-iot",
    text: "Lokalizatory GPS i czujniki IoT",
  },
  {
    href: "/audyt-elektroniki-i-firmware",
    text: "Audyt elektroniki i firmware",
  },
  { href: "/bluetooth-low-energy", text: "Bluetooth Low Energy" },
  { href: "/blog", text: "Blog" },
  { href: "/projects", text: "Projekty" },
] as const;

export const iotKnowledgeBaseLinks = [
  {
    href: "/pl/blogs/ile-kosztuje-urzadzenie-iot",
    text: "Ile kosztuje urządzenie IoT?",
  },
  {
    href: "/pl/blogs/co-to-jest-internet-rzeczy-iot",
    text: "Co to jest IoT? Internet rzeczy w praktyce",
  },
  {
    href: "/pl/blogs/lte-m-vs-nb-iot",
    text: "LTE-M vs NB-IoT - jaką łączność wybrać?",
  },
  {
    href: "/pl/blogs/jak-powstaje-prototyp-urzadzenia-elektronicznego",
    text: "Jak powstaje prototyp urządzenia elektronicznego?",
  },
  {
    href: "/pl/blogs/arduino-esp32-stm32-vs-nordic-nrf",
    text: "Arduino, ESP32, STM32 czy Nordic nRF?",
  },
] as const;

export const organizationStructuredData = (description: string) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Blues Brackets",
  url: "https://bluesbrackets.com",
  logo: "https://bluesbrackets.com/favicon-dark.png",
  description,
  areaServed: "EU",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "hello@bluesbrackets.com",
    availableLanguage: ["Polish", "English"],
  },
});

export const serviceStructuredData = (
  name: string,
  description: string,
  serviceType: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  provider: {
    "@type": "Organization",
    name: "Blues Brackets",
  },
  description,
  areaServed: "EU",
  serviceType,
});

export const faqStructuredData = (
  faqs: readonly { q: string; a: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});
